import { useState, useRef, useEffect } from 'react';
import transactionService from '../services/transactionService';
import walletService from '../../wallet/services/walletService';
import { toast } from 'react-hot-toast';

const TransactionImport = ({ onImportSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [jsonData, setJsonData] = useState('');
  const [parsedTransactions, setParsedTransactions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const fileInputRef = useRef(null);
  
  // Fetch available accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setLoadingAccounts(true);
        const response = await walletService.getAccounts();
        
        if (response?.success && response?.data) {
          // Handle both array and object responses
          const accountsData = Array.isArray(response.data) 
            ? response.data 
            : response.data.accounts || [];
          
          setAccounts(accountsData);
          
          // Set first account as default if available
          if (accountsData.length > 0) {
            setSelectedAccount(accountsData[0]._id);
          }
        } else {
          toast.error('Failed to load accounts');
        }
      } catch (error) {
        console.error('Error fetching accounts:', error);
        toast.error('Failed to load accounts');
      } finally {
        setLoadingAccounts(false);
      }
    };
    
    fetchAccounts();
  }, []);
  
  // Handle CSV file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setIsLoading(true);
    
    try {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const csvData = e.target.result;
        const transactions = parseCSV(csvData);
        setParsedTransactions(transactions);
        setShowPreview(true);
        setIsLoading(false);
      };
      
      reader.readAsText(file);
    } catch (error) {
      console.error('Error reading file:', error);
      toast.error('Failed to read file. Please try again.');
      setIsLoading(false);
    }
  };
  
  // Parse CSV data into transaction objects
  const parseCSV = (csvData) => {
    // Basic CSV parsing (can be improved with a proper CSV library)
    const lines = csvData.split('\n');
    const headers = lines[0].split(',').map(h => h.trim());
    
    const dateIndex = headers.findIndex(h => h.toLowerCase().includes('date'));
    const descriptionIndex = headers.findIndex(h => h.toLowerCase().includes('description'));
    const amountIndex = headers.findIndex(h => 
      h.toLowerCase().includes('amount') || 
      h.toLowerCase().includes('debit') || 
      h.toLowerCase().includes('credit')
    );
    
    const transactions = [];
    
    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue;
      
      const values = lines[i].split(',').map(v => v.trim());
      
      if (values.length >= Math.max(dateIndex, descriptionIndex, amountIndex) + 1) {
        let amount = parseFloat(values[amountIndex].replace(/[^0-9.-]/g, ''));
        
        // If this is a credit/debit system, handle accordingly
        if (headers[amountIndex].toLowerCase().includes('debit')) {
          amount = -Math.abs(amount);
        }
        
        transactions.push({
          date: new Date(values[dateIndex]),
          description: values[descriptionIndex],
          amount: amount,
          account: selectedAccount
        });
      }
    }
    
    return transactions;
  };
  
  // Handle JSON data parsing
  const handleJsonParse = () => {
    try {
      const data = JSON.parse(jsonData);
      let transactions = [];
      
      if (Array.isArray(data)) {
        transactions = data.map(tx => ({
          ...tx,
          date: tx.date ? new Date(tx.date) : new Date(),
          amount: parseFloat(tx.amount),
          account: selectedAccount || tx.account || null
        }));
      } else {
        toast.error('Invalid JSON format. Expected an array of transactions.');
        return;
      }
      
      setParsedTransactions(transactions);
      setShowPreview(true);
    } catch (error) {
      console.error('Error parsing JSON:', error);
      toast.error('Failed to parse JSON. Please check your format.');
    }
  };
  
  // Submit imported transactions
  const handleImport = async () => {
    if (!parsedTransactions.length) {
      toast.error('No transactions to import.');
      return;
    }
    
    if (!selectedAccount) {
      toast.error('Please select an account for these transactions.');
      return;
    }
    
    setIsLoading(true);
    
    // Add account to each transaction that doesn't have one
    const transactionsWithAccount = parsedTransactions.map(tx => ({
      ...tx,
      account: tx.account || selectedAccount
    }));
    
    try {
      const result = await transactionService.importTransactions(transactionsWithAccount);
      
      if (result.success) {
        toast.success(`Successfully imported ${result.data.transactions.length} transactions!`);
        setJsonData('');
        setParsedTransactions([]);
        setShowPreview(false);
        
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        
        if (onImportSuccess) {
          onImportSuccess(result.data.transactions);
        }
      } else {
        toast.error('Failed to import transactions: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('An error occurred during import.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-semibold mb-4">Import Transactions</h2>
      
      {/* Account Selection */}
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">Select Account</label>
        {loadingAccounts ? (
          <div className="animate-pulse h-10 bg-gray-200 rounded"></div>
        ) : (
          <select
            className="w-full border-2 border-gray-300 p-2 rounded bg-white text-gray-700"
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            disabled={isLoading}
          >
            <option value="">-- Select Account --</option>
            {accounts.map(account => (
              <option key={account._id} value={account._id}>
                {account.bankName} - {account.lastFourDigits} ({account.accountType})
              </option>
            ))}
          </select>
        )}
        <p className="text-gray-500 text-sm mt-1">
          All imported transactions will be associated with this account
        </p>
      </div>
      
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">Upload CSV File</label>
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          className="w-full border-2 border-gray-300 p-2 rounded bg-white"
          onChange={handleFileUpload}
          disabled={isLoading || !selectedAccount}
        />
        <p className="text-gray-500 text-sm mt-1">
          CSV should include date, description, and amount columns
        </p>
      </div>
      
      <div className="mb-6">
        <label className="block text-gray-700 font-medium mb-2">Or Paste JSON Data</label>
        <textarea
          className="w-full border-2 border-gray-300 p-2 rounded h-40 bg-white text-gray-700"
          value={jsonData}
          onChange={(e) => setJsonData(e.target.value)}
          placeholder='[{"date": "2023-06-15", "description": "Grocery Store", "amount": -45.67}]'
          disabled={isLoading || !selectedAccount}
        />
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded mt-2 hover:bg-blue-600 disabled:opacity-50"
          onClick={handleJsonParse}
          disabled={isLoading || !jsonData || !selectedAccount}
        >
          Parse JSON
        </button>
      </div>
      
      {showPreview && parsedTransactions.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-medium mb-2">Preview ({parsedTransactions.length} transactions)</h3>
          <div className="max-h-80 overflow-y-auto border border-gray-200 rounded">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {parsedTransactions.slice(0, 10).map((tx, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">
                      {new Date(tx.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">
                      {tx.description}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-800">
                      <span className={tx.amount < 0 ? "text-red-600" : "text-green-600"}>
                        ${Math.abs(tx.amount).toFixed(2)}
                      </span>
                    </td>
                  </tr>
                ))}
                {parsedTransactions.length > 10 && (
                  <tr>
                    <td colSpan={3} className="px-4 py-2 text-sm text-gray-500 text-center">
                      ...and {parsedTransactions.length - 10} more
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      <div className="flex justify-end">
        <button
          className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 disabled:opacity-50"
          onClick={handleImport}
          disabled={isLoading || !parsedTransactions.length || !selectedAccount}
        >
          {isLoading ? 'Importing...' : `Import ${parsedTransactions.length} Transactions`}
        </button>
      </div>
    </div>
  );
};

export default TransactionImport; 